import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Shield, Copy, Download, AlertTriangle, CheckCircle2, Smartphone } from 'lucide-react';

interface TwoFactorAuthModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isEnabled: boolean;
  onStatusChange: () => void;
}

export function TwoFactorAuthModal({ open, onOpenChange, isEnabled, onStatusChange }: TwoFactorAuthModalProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<'initial' | 'setup' | 'verify' | 'recovery'>('initial');
  const [qrCode, setQrCode] = useState<string>('');
  const [secret, setSecret] = useState<string>('');
  const [verifyCode, setVerifyCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [factorId, setFactorId] = useState<string>('');

  useEffect(() => {
    if (open && !isEnabled) {
      setStep('initial');
    }
  }, [open, isEnabled]);

  const handleEnroll = async () => {
    setLoading(true);
    try {
      // Clean up any leftover unverified TOTP factors from previous attempts
      const { data: existing, error: listError } = await supabase.auth.mfa.listFactors();
      if (listError) throw listError;

      const totpFactors = existing?.totp ?? [];
      for (const f of totpFactors) {
        if (f.status !== 'verified') {
          await supabase.auth.mfa.unenroll({ factorId: f.id });
        }
      }

      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: `Authenticator App ${Date.now()}`
      });

      if (error) throw error;

      if (data) {
        setFactorId(data.id);
        setSecret(data.totp.secret);
        
        // Supabase already returns a renderable SVG/data URL for the QR code.
        setQrCode(data.totp.qr_code);
        
        setStep('setup');
      }
    } catch (error) {
      console.error('Error enrolling MFA:', error);
      toast({
        title: "Setup failed",
        description: "Failed to initialize 2FA setup. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (!verifyCode || verifyCode.length !== 6) {
      toast({
        title: "Invalid code",
        description: "Please enter a valid 6-digit code.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      // Use challengeAndVerify for enrollment verification
      const { error: verifyError } = await supabase.auth.mfa.challengeAndVerify({
        factorId: factorId,
        code: verifyCode
      });

      if (verifyError) throw verifyError;

      // Generate recovery codes
      const codes = Array.from({ length: 8 }, () => 
        Math.random().toString(36).substring(2, 10).toUpperCase()
      );
      setRecoveryCodes(codes);
      
      setStep('recovery');
      onStatusChange();
      
      toast({
        title: "2FA enabled successfully",
        description: "Your account is now protected with two-factor authentication.",
      });

      // Send email notification
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user?.email) {
          await supabase.functions.invoke('send-2fa-notification', {
            body: {
              email: user.email,
              userName: user.user_metadata?.full_name || 'Voyager',
              action: 'enabled'
            }
          });
        }
      } catch (emailError) {
        console.error('Failed to send 2FA notification email:', emailError);
        // Don't fail the whole operation if email fails
      }
    } catch (error) {
      console.error('Error verifying MFA:', error);
      toast({
        title: "Verification failed",
        description: "Invalid code. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async () => {
    setLoading(true);
    try {
      const { data: factors, error: listError } = await supabase.auth.mfa.listFactors();
      
      if (listError) throw listError;

      if (factors && factors.totp && factors.totp.length > 0) {
        const { error: unenrollError } = await supabase.auth.mfa.unenroll({
          factorId: factors.totp[0].id
        });

        if (unenrollError) throw unenrollError;

        toast({
          title: "2FA disabled",
          description: "Two-factor authentication has been removed from your account.",
        });

        // Send email notification
        try {
          const { data: { user } } = await supabase.auth.getUser();
          if (user?.email) {
            await supabase.functions.invoke('send-2fa-notification', {
              body: {
                email: user.email,
                userName: user.user_metadata?.full_name || 'Voyager',
                action: 'disabled'
              }
            });
          }
        } catch (emailError) {
          console.error('Failed to send 2FA notification email:', emailError);
          // Don't fail the whole operation if email fails
        }
        
        onStatusChange();
        onOpenChange(false);
      }
    } catch (error) {
      console.error('Error disabling MFA:', error);
      toast({
        title: "Failed to disable",
        description: "Could not disable 2FA. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const copySecret = () => {
    navigator.clipboard.writeText(secret);
    toast({
      title: "Copied",
      description: "Secret key copied to clipboard.",
    });
  };

  const downloadRecoveryCodes = () => {
    const text = `OdyVoy Recovery Codes\n\nSave these codes in a secure location.\n\n${recoveryCodes.join('\n')}`;
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'odyvoy-recovery-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast({
      title: "Downloaded",
      description: "Recovery codes saved to your device.",
    });
  };

  const copyRecoveryCodes = () => {
    navigator.clipboard.writeText(recoveryCodes.join('\n'));
    toast({
      title: "Copied",
      description: "Recovery codes copied to clipboard.",
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg sm:text-xl">
            <Shield className="h-5 w-5 flex-shrink-0" />
            Two-Factor Authentication
          </DialogTitle>
          <DialogDescription className="text-sm">
            {isEnabled ? 
              "Manage your two-factor authentication settings" : 
              "Add an extra layer of security to your account"
            }
          </DialogDescription>
        </DialogHeader>

        {step === 'initial' && !isEnabled && (
          <div className="space-y-6">
            <div className="flex items-start gap-3 p-4 bg-primary/5 rounded-lg border border-primary/20">
              <Shield className="h-5 w-5 text-primary mt-0.5" />
              <div className="space-y-2">
                <p className="text-sm font-medium">Enhanced Security</p>
                <p className="text-sm text-muted-foreground">
                  Two-factor authentication adds an extra layer of security by requiring both your password 
                  and a verification code from your phone to sign in.
                </p>
              </div>
            </div>

            <div className="space-y-3">
              <h4 className="font-medium text-sm">What you'll need:</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-center gap-2">
                  <Smartphone className="h-4 w-4" />
                  An authenticator app (Google Authenticator, Authy, etc.)
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4" />
                  Access to this device to scan the QR code
                </li>
              </ul>
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
              <Button onClick={handleEnroll} disabled={loading} className="flex-1 min-h-[44px]">
                {loading ? 'Setting up...' : 'Enable 2FA'}
              </Button>
              <Button variant="outline" onClick={() => onOpenChange(false)} className="min-h-[44px]">
                Cancel
              </Button>
            </div>
          </div>
        )}

        {step === 'setup' && (
          <div className="space-y-6">
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Scan this QR code with your authenticator app:
              </p>
              
              {qrCode && (
                <div className="flex justify-center p-4 bg-white rounded-lg">
                  <img src={qrCode} alt="QR Code" className="w-48 h-48" />
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Or enter this key manually:</Label>
                <div className="flex gap-2">
                  <Input value={secret} readOnly className="font-mono text-sm" />
                  <Button variant="outline" size="icon" onClick={copySecret}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>

            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription className="text-xs">
                Save your secret key in a secure location. You'll need it to set up 2FA on a new device.
              </AlertDescription>
            </Alert>

            <Button onClick={() => setStep('verify')} className="w-full min-h-[44px]">
              Continue to Verification
            </Button>
          </div>
        )}

        {step === 'verify' && (
          <div className="space-y-6">
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Enter the 6-digit code from your authenticator app:
              </p>
              
              <div className="space-y-2">
                <Label htmlFor="verify-code">Verification Code</Label>
                <Input
                  id="verify-code"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                  className="text-center text-2xl tracking-widest font-mono"
                />
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
              <Button onClick={handleVerify} disabled={loading || verifyCode.length !== 6} className="flex-1 min-h-[44px]">
                {loading ? 'Verifying...' : 'Verify & Enable'}
              </Button>
              <Button variant="outline" onClick={() => setStep('setup')} className="min-h-[44px]">
                Back
              </Button>
            </div>
          </div>
        )}

        {step === 'recovery' && (
          <div className="space-y-6">
            <Alert className="border-amber-500/50 bg-amber-500/10">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              <AlertDescription className="text-sm">
                <strong>Important:</strong> Save these recovery codes in a secure location. 
                You can use them to access your account if you lose your device.
              </AlertDescription>
            </Alert>

            <div className="space-y-3">
              <Label className="text-sm font-medium">Recovery Codes</Label>
              <div className="grid grid-cols-2 gap-2 p-4 bg-muted rounded-lg font-mono text-sm">
                {recoveryCodes.map((code, index) => (
                  <div key={index} className="text-center py-1">
                    {code}
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
              <Button variant="outline" onClick={downloadRecoveryCodes} className="flex-1 min-h-[44px]">
                <Download className="h-4 w-4 mr-2" />
                Download
              </Button>
              <Button variant="outline" onClick={copyRecoveryCodes} className="flex-1 min-h-[44px]">
                <Copy className="h-4 w-4 mr-2" />
                Copy
              </Button>
            </div>

            <Button onClick={() => onOpenChange(false)} className="w-full min-h-[44px]">
              Done
            </Button>
          </div>
        )}

        {isEnabled && step === 'initial' && (
          <div className="space-y-6">
            <div className="flex items-center justify-center p-6 bg-green-500/10 rounded-lg border border-green-500/20">
              <div className="text-center space-y-2">
                <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto" />
                <p className="font-medium">2FA is Active</p>
                <p className="text-sm text-muted-foreground">
                  Your account is protected with two-factor authentication
                </p>
              </div>
            </div>

            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription className="text-sm">
                Disabling 2FA will make your account less secure. Only do this if you're certain.
              </AlertDescription>
            </Alert>

            <div className="flex flex-col sm:flex-row gap-2">
              <Button 
                variant="destructive" 
                onClick={handleDisable} 
                disabled={loading}
                className="flex-1 min-h-[44px]"
              >
                {loading ? 'Disabling...' : 'Disable 2FA'}
              </Button>
              <Button variant="outline" onClick={() => onOpenChange(false)} className="min-h-[44px]">
                Cancel
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
